import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMyDailyDirectClient } from "@mydailydirect/app-kit";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  ProgressSteps,
  InteractiveLoader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatPaise,
  useToast,
  TaxInvoiceModal,
} from "@mydailydirect/ui";
import {
  ArrowLeft,
  Download,
  RotateCcw,
  ShieldCheck,
  Store,
  Package,
  RefreshCw,
  AlertCircle,
  Truck,
  CheckCircle2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { orderStatusMeta } from "../lib/orderStatus";
import { useCart } from "../lib/cart";

export function OrderDetailPage() {
  const client = useMyDailyDirectClient();
  const navigate = useNavigate();
  const cart = useCart();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);

  const handleGoBack = () => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      typeof window.history.state.idx === "number" &&
      window.history.state.idx > 0
    ) {
      navigate(-1);
    } else {
      navigate("/orders");
    }
  };

  const orderQuery = useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => client.orders.get(orderId),
    enabled: Number.isFinite(orderId) && orderId > 0,
    retry: 2,
  });

  const skusQuery = useQuery({
    queryKey: ["skus"],
    queryFn: () => client.skus.list(),
    staleTime: 60_000,
  });

  // Guard routes lookup so it never crashes even if routes have null stops or if endpoint fails
  const routesQuery = useQuery({
    queryKey: ["routes"],
    queryFn: () => client.routes.list(),
    retry: false,
    staleTime: 60_000,
  });

  const matchedStop = (routesQuery.data ?? [])
    .flatMap((r) => r?.stops ?? [])
    .find((s) => Boolean(s && s.orderId === orderId));

  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);

  // Loading state with clear navigation
  if (orderQuery.isLoading) {
    return (
      <div className="space-y-6 pb-12 px-1 sm:px-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="h-8 px-2 font-bold gap-1.5"
          >
            <Store className="h-4 w-4 text-primary" /> Catalog
          </Button>
          <span>/</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/orders")}
            className="h-8 px-2 font-semibold gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> My Orders
          </Button>
        </div>
        <InteractiveLoader
          title="Loading Order Details & Dispatch Status..."
          subtitle="Syncing live QC-verified dispatch manifest, delivery slot, and invoice items"
        />
      </div>
    );
  }

  // Graceful Error State
  if (orderQuery.isError || !orderQuery.data) {
    return (
      <div className="space-y-6 pb-12 px-1 sm:px-0">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="font-bold text-xs sm:text-sm text-muted-foreground hover:text-foreground gap-1.5 rounded-control -ml-2"
          >
            <Store className="h-4 w-4 text-primary" /> Back to Catalog
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGoBack}
            className="font-semibold text-xs gap-1.5 rounded-control"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Orders
          </Button>
        </div>

        <Card className="p-8 text-center border-border rounded-card max-w-lg mx-auto space-y-4 shadow-sm bg-card">
          <div className="mx-auto w-12 h-12 rounded-full bg-tone-warning-soft text-tone-warning-text flex items-center justify-center">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground">Order Details Unavailable</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Could not load details for Order #{orderId}. It may have been archived or network is unreachable.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button
              variant="primary"
              onClick={() => navigate("/")}
              className="font-bold shadow-xs bg-cta text-cta-foreground hover:brightness-105 rounded-control"
            >
              <Store className="h-4 w-4 mr-2" /> Back to Catalog Listing
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/orders")}
              className="font-semibold rounded-control"
            >
              <Package className="h-4 w-4 mr-2" /> View All Orders
            </Button>
            <Button
              variant="secondary"
              onClick={() => orderQuery.refetch()}
              className="font-semibold rounded-control"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const order = orderQuery.data;
  const lines = order.lines ?? [];
  const meta = orderStatusMeta(order.status || "PLACED");
  const badgeVariant = ((meta?.variant || "default") as string).replace("pulse", "").toLowerCase() as
    | "success"
    | "warning"
    | "default"
    | "destructive";

  const skuName = (skuId: number) =>
    skusQuery.data?.find((s) => s.id === skuId)?.name ?? `SKU #${skuId}`;

  // Delivery payment collection details
  const lmPaymentMethod = matchedStop?.paymentMethod || (order as any)?.paymentMethod || "CASH";
  const lmAmountCollected = matchedStop?.amountCollectedPaise ?? order.totalPaise ?? 0;
  const lmPodNote = matchedStop?.podNote;
  const lmShortage = matchedStop?.shortagePaise;

  const getLmMethodDetails = (method: string) => {
    const m = (method || "").toUpperCase();
    if (m === "CASH") return { label: "Cash", icon: "💵" };
    if (m === "QR" || m === "UPI") return { label: "UPI QR", icon: "📱" };
    if (m === "SPLIT") return { label: "Split Payment (Cash + QR)", icon: "💳" };
    if (m === "MULTI") return { label: "Multi-Instrument Payment", icon: "📑" };
    return { label: method || "Payment", icon: "💰" };
  };

  // ProgressSteps calculation
  const steps = ["Order Placed", "Confirmed", "Packed", "Out for Delivery", "Delivered"];
  let currentStep = 0;
  if (order.status === "CONFIRMED") currentStep = 1;
  else if (order.status === "LINED") currentStep = 2;
  else if (order.status === "LOADED" || order.status === "INVOICED") currentStep = 3;
  else if (order.status === "DELIVERED" || order.status === "SETTLED") currentStep = 4;

  const handleDownloadInvoice = () => {
    setIsInvoiceOpen(true);
  };

  const handleReorderThis = () => {
    if (lines.length === 0) return;

    lines.forEach((line) => {
      const currentQty = cart.qtyOf(line.skuId, line.packId);
      cart.setQty(line.skuId, currentQty + line.qty, line.packId);
    });

    toast({
      title: "Order Items Copied to Cart!",
      description: `Copied ${lines.length} items from Order #${order.id}.`,
      variant: "success",
    });

    window.dispatchEvent(new CustomEvent("mdd:open-cart"));
    navigate("/");
  };

  const placedDateStr = order.placedAt
    ? new Date(order.placedAt).toLocaleString("en-IN", {
        dateStyle: "full",
        timeStyle: "short",
      })
    : "Recently placed";

  return (
    <div className="space-y-5 pb-16 px-1 sm:px-0">
      {/* Prominent Breadcrumb & Quick Navigation Bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="h-8 px-2 font-bold text-foreground hover:text-primary gap-1.5 rounded-control -ml-2"
          >
            <Store className="h-4 w-4 text-primary" /> Catalog
          </Button>
          <span>/</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoBack}
            className="h-8 px-2 font-semibold text-muted-foreground hover:text-foreground gap-1.5 rounded-control"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Button>
          <span>/</span>
          <span className="font-extrabold text-foreground">Order #{order.id}</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/")}
            className="font-bold text-xs gap-1.5 rounded-control border-border hover:bg-muted"
          >
            <Store className="h-3.5 w-3.5 text-cta" /> Back to Catalog
          </Button>
        </div>
      </div>

      <PageHeader
        title={`Order #${order.id}`}
        description={`Placed on ${placedDateStr}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto">
            <Button
              variant="primary"
              onClick={handleReorderThis}
              className="font-bold shadow-sm bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99] rounded-control min-h-[44px] flex-1 sm:flex-initial"
            >
              <RotateCcw className="h-4 w-4 mr-2" /> Reorder This
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadInvoice}
              className="font-bold rounded-control min-h-[44px] border-border hover:bg-muted flex-1 sm:flex-initial"
            >
              <Download className="h-4 w-4 mr-2" /> GST Invoice
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate("/orders")}
              className="font-bold rounded-control min-h-[44px] bg-sunken hover:bg-muted text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          </div>
        }
      />

      {/* ProgressSteps Delivery Workflow Component */}
      <Card className="p-4 sm:p-6 bg-card border-border rounded-card shadow-2xs">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-2xs font-extrabold text-muted-foreground uppercase tracking-wider">
            Order Fulfillment Progress
          </h3>
          <Badge dot variant={badgeVariant === "default" || !badgeVariant ? "default" : (badgeVariant as any)}>
            {meta.label}
          </Badge>
        </div>

        <ProgressSteps steps={steps} currentStep={currentStep} className="py-2" />
      </Card>

      {/* Buyer Payment & Delivery Instructions Card */}
      <div className="rounded-card border border-border bg-card p-4 sm:p-5 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm font-semibold text-foreground">Status:</span>
            <Badge dot variant={badgeVariant === "default" || !badgeVariant ? "default" : (badgeVariant as any)}>
              {meta.label}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm font-semibold text-foreground">Payment:</span>
            <Badge
              variant={order.status === "DELIVERED" || order.status === "SETTLED" || (order as any).paymentStatus === "PAID" ? "success" : "outline"}
              className="font-extrabold uppercase text-2xs"
            >
              {order.status === "DELIVERED" || order.status === "SETTLED" || (order as any).paymentStatus === "PAID"
                ? `Paid (${lmPaymentMethod})`
                : (order as any).paymentMethod || "Pay on Delivery"}
            </Badge>
          </div>
        </div>

        {/* Clear Buyer Instructions depending on order & payment status */}
        {order.status === "DELIVERED" || order.status === "SETTLED" || matchedStop?.status === "DELIVERED" ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-control bg-tone-success-soft border border-tone-success-border p-3.5 text-tone-success-text">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tone-success-text text-white font-bold text-sm">
                ✓
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-extrabold text-sm sm:text-base">Payment Collected & Order Delivered</div>
                  <Badge variant="success" className="font-extrabold uppercase px-2 py-0.5 text-2xs">
                    {lmPaymentMethod}
                  </Badge>
                </div>
                <div className="text-xs opacity-90">
                  Payment of <strong>{formatPaise(lmAmountCollected)}</strong> received via <strong>{getLmMethodDetails(lmPaymentMethod).label}</strong>.
                </div>
              </div>
            </div>

            {/* Delivery Payment Breakdown Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
              <div className="p-3 rounded-control bg-sunken border border-border space-y-1">
                <span className="text-muted-foreground block font-medium">Payment Mode</span>
                <span className="font-bold text-xs sm:text-sm text-foreground flex items-center gap-1.5">
                  <span className="text-base">{getLmMethodDetails(lmPaymentMethod).icon}</span>
                  {getLmMethodDetails(lmPaymentMethod).label}
                </span>
              </div>

              <div className="p-3 rounded-control bg-sunken border border-border space-y-1">
                <span className="text-muted-foreground block font-medium">Amount Paid</span>
                <span className="font-bold text-xs sm:text-sm text-tone-success-text block type-num">
                  {formatPaise(lmAmountCollected)}
                  {lmShortage && lmShortage > 0 ? (
                    <span className="text-2xs text-tone-warning-text font-semibold ml-1 block">
                      (Balance: {formatPaise(lmShortage)})
                    </span>
                  ) : null}
                </span>
              </div>

              <div className="p-3 rounded-control bg-sunken border border-border space-y-1">
                <span className="text-muted-foreground block font-medium">Delivery Notes</span>
                <span className="font-medium text-xs text-foreground italic line-clamp-2">
                  {lmPodNote || "Delivered successfully to shop front."}
                </span>
              </div>
            </div>
          </div>
        ) : (order as any).paymentStatus === "PAID" || (order as any).paymentMethod === "UPI" ? (
          <div className="flex items-start gap-3 rounded-control bg-tone-success-soft border border-tone-success-border p-3.5 text-tone-success-text">
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-extrabold text-sm sm:text-base">Payment Completed Upfront</div>
              <div className="text-xs mt-0.5 opacity-90 leading-relaxed">
                You paid <strong>{formatPaise(order.totalPaise)}</strong> via Instant UPI QR. No payment is required upon delivery. Please present Order #{order.id} to the delivery driver when goods arrive.
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-control bg-tone-brand-soft border border-tone-brand-border p-3.5 text-tone-brand-text">
            <Truck className="h-5 w-5 shrink-0 mt-0.5 text-cta" />
            <div>
              <div className="font-extrabold text-sm sm:text-base text-foreground">
                Pay on Morning Delivery ({formatPaise(order.totalPaise)})
              </div>
              <div className="text-xs mt-0.5 text-muted-foreground leading-relaxed">
                Please have <strong>Cash</strong> or your <strong>UPI App</strong> ready. The delivery driver will arrive between 6:00 AM – 9:00 AM with your dispatch.
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-12">
        {/* Table / Card list of Order Items (Responsive) */}
        <Card className="lg:col-span-8 border-border rounded-card overflow-hidden bg-card">
          <CardHeader className="bg-sunken border-b border-border p-4 sm:p-5">
            <CardTitle className="text-sm sm:text-base font-extrabold flex items-center justify-between">
              <span>Order Line Items ({lines.length})</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReorderThis}
                className="text-xs font-bold text-cta hover:text-cta/80"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Add All to Cart
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Mobile View: Clean Card List */}
            <div className="sm:hidden divide-y divide-border">
              {lines.map((line) => (
                <div key={line.id ?? `${line.skuId}-${line.packId ?? 0}`} className="p-4 space-y-1.5">
                  <div className="font-bold text-sm text-foreground flex items-center gap-1.5 flex-wrap">
                    <span>{skuName(line.skuId)}</span>
                    {line.packLabel && (
                      <span className="text-[10px] font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded">
                        {line.packLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Quantity: <strong className="text-foreground font-mono">{line.qty} {line.packLabel ? "packs" : ""}</strong></span>
                    <span>Rate: {line.unitPricePaise != null ? formatPaise(line.unitPricePaise) : "—"}</span>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t border-border/50 text-xs">
                    <span className="font-medium text-muted-foreground">Subtotal:</span>
                    <span className="font-black text-sm text-foreground type-num">
                      {line.lineTotalPaise != null ? formatPaise(line.lineTotalPaise) : "—"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop / Tablet View: Full Table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">Product Item</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right pr-6">Subtotal</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id ?? `${line.skuId}-${line.packId ?? 0}`}>
                      <TableCell className="pl-6 font-semibold">
                        <div className="flex items-center gap-2">
                          <span>{skuName(line.skuId)}</span>
                          {line.packLabel && (
                            <span className="text-[10px] font-bold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded">
                              {line.packLabel}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium font-mono">
                        {line.qty} {line.packLabel ? "packs" : ""}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground type-num">
                        {line.unitPricePaise != null ? formatPaise(line.unitPricePaise) : "—"}
                      </TableCell>
                      <TableCell className="text-right pr-6 font-bold text-foreground type-num">
                        {line.lineTotalPaise != null ? formatPaise(line.lineTotalPaise) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between sm:justify-end items-center p-4 sm:p-5 border-t border-border bg-sunken">
              <div className="flex items-center gap-3 sm:gap-4">
                <span className="text-xs sm:text-sm font-bold text-muted-foreground">Total Payable Amount:</span>
                <span className="text-xl sm:text-2xl font-black text-foreground type-num">{formatPaise(order.totalPaise)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details Sidebar Card */}
        <Card className="lg:col-span-4 h-fit border-border rounded-card bg-card">
          <CardHeader className="p-4 sm:p-5 border-b border-border">
            <CardTitle className="text-sm sm:text-base font-extrabold">Order Metadata</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-5 space-y-3.5 text-xs">
            <div>
              <div className="text-muted-foreground mb-1">Retailer Account ID</div>
              <div className="font-extrabold text-sm text-foreground font-mono">#{order.retailerId}</div>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="text-muted-foreground mb-1">Placed Timestamp</div>
              <div className="font-semibold text-foreground">
                {placedDateStr}
              </div>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="text-muted-foreground mb-1">Fulfillment Guarantee</div>
              <div className="font-bold text-tone-success-text flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" />
                <span>100% Quality & Weight Protected</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <TaxInvoiceModal
        open={isInvoiceOpen}
        onClose={() => setIsInvoiceOpen(false)}
        orderId={order.id}
        placedAt={order.placedAt}
        lines={lines.map((l) => {
          const sku = skusQuery.data?.find((s) => s.id === l.skuId);
          const price =
            l.unitPricePaise ??
            (l as any).pricePaise ??
            (l.lineTotalPaise ? Math.round(l.lineTotalPaise / (l.qty || 1)) : undefined) ??
            sku?.pricePaise ??
            0;
          return {
            id: l.skuId,
            code: sku?.code ?? `SKU-${l.skuId}`,
            name: skuName(l.skuId),
            gstRate: sku?.gstPercent ?? 5,
            qty: l.qty,
            pricePaise: Number(price) || 0,
          };
        })}
      />
    </div>
  );
}
